import { DebugRenderer } from "./debugRenderer";
import { TileManager } from "./tileManager";

export interface TileInfo {
    tileId: string;
    layerIndex: number;
    lastAccessed: number;
    level: number;
    col: number;
    row: number;
}

// Manages the texture atlas, tile slots, and indirection table
export class SvtManager {

    private readonly MAX_LAYERS: number = 128; // TODO: compute this

    private gl: WebGL2RenderingContext;
    private tileManager: TileManager;
    private tileSize: number;
    private atlasSize: number;
    private svtDepth: number;

    private atlasTexture: WebGLTexture;
    private indirectionTable: WebGLTexture;

    private activeTiles: Map<string, TileInfo>;
    private layerAssignment: (string | null)[];

    private tileIdCache: Map<string, [number, number, number]>;
    private uploadQueue: Array<{ tileId: string; tilePath: string; tileInfo: TileInfo }>;

    constructor(gl: WebGL2RenderingContext, tileManager: TileManager, tileSize: number, atlasSize: number, svtDepth: number) {
        this.gl = gl;
        this.tileManager = tileManager;
        this.tileSize = tileSize;
        this.atlasSize = atlasSize;
        this.svtDepth = svtDepth;

        this.activeTiles = new Map();
        this.layerAssignment = new Array(this.MAX_LAYERS).fill(null);
        this.tileIdCache = new Map();
        this.uploadQueue = [];

        // texture array
        this.atlasTexture = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasTexture);
        gl.texImage3D(gl.TEXTURE_2D_ARRAY, 0, gl.RGBA8, tileSize, tileSize, this.MAX_LAYERS, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // indirection table
        this.indirectionTable = gl.createTexture()!;
        const indirectionSize = 2 ** svtDepth;
        gl.bindTexture(gl.TEXTURE_2D, this.indirectionTable);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, indirectionSize, indirectionSize, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        gl.generateMipmap(gl.TEXTURE_2D);
    }

    // Reserves a free slot in the atlas or evicts an LRU tile if full.
    reserveLayer(): number {
        for (let i = 1; i < this.MAX_LAYERS; i++) {
            if (this.layerAssignment[i] === null) {
                return i;
            }
        }
        return this.evictLRUTile();
    }

    scheduleTileUpload(tileId: string, tilePath: string): void {
        if (this.activeTiles.has(tileId)) {
            this.touchTile(tileId); // Mark as recently used
            return;
        }
        const layerIndex = this.reserveLayer();
        const [level, col, row] = this.parseTileId(tileId);
        const tileInfo: TileInfo = { tileId, layerIndex, lastAccessed: Date.now(), level, col, row };
        this.activeTiles.set(tileId, tileInfo);
        this.layerAssignment[layerIndex] = tileId;
        this.uploadQueue.push({ tileId, tilePath, tileInfo });
    }

    async processUploadQueue(limit: number = 2): Promise<void> {
        const uploads = this.uploadQueue.splice(0, limit);
        for (const upload of uploads) {
            await this.uploadTileNow(upload.tileId, upload.tilePath, upload.tileInfo);
        }
    }

    // Uploads a tile to the atlas and updates the indirection table.
    async uploadTileNow(tileId: string, tilePath: string, tileInfo: TileInfo): Promise<ImageBitmap> {
        console.log("uploadTile", tileId, tileInfo.layerIndex);
        const { gl, tileManager, tileSize, atlasTexture, indirectionTable, svtDepth } = this;
        const image = await tileManager.retrieveTile(tileId, tilePath);

        gl.bindTexture(gl.TEXTURE_2D_ARRAY, atlasTexture);
        gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, tileInfo.layerIndex, tileSize, tileSize, 1, gl.RGBA, gl.UNSIGNED_BYTE, image);

        const mipLevel = svtDepth - tileInfo.level;
        const indirectionData = new Uint8Array([tileInfo.layerIndex, 0, tileInfo.level, 255]);
        gl.bindTexture(gl.TEXTURE_2D, indirectionTable);
        gl.texSubImage2D(gl.TEXTURE_2D, mipLevel, tileInfo.col, tileInfo.row, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, indirectionData);
        return image;
    }

    // Evicts the least recently used tile (excluding level 0).
    private evictLRUTile(): number {
        const { gl, svtDepth, activeTiles, layerAssignment, indirectionTable } = this;
        const [tileId, info] = this.activeTiles.entries().next().value;
        if (!tileId) {
            throw new Error("No tiles to evict");
        }
        activeTiles.delete(tileId);
        const layerIndex = info.layerIndex;
        layerAssignment[layerIndex] = null;
        const mipLevel = svtDepth - info.level;
        const indirectionData = new Uint8Array([0, 0, 0, 0]);
        gl.bindTexture(gl.TEXTURE_2D, indirectionTable);
        gl.texSubImage2D(gl.TEXTURE_2D, mipLevel, info.col, info.row, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, indirectionData);
        console.log(`Evicted tile: ${tileId}, layer: ${layerIndex}`);
        return layerIndex;
    }

    // Parses tile ID into [level, column, row] and caches it
    // TODO: rethink this whole thing
    private parseTileId(tileId: string): [number, number, number] {
        if (this.tileIdCache.has(tileId)) {
            return this.tileIdCache.get(tileId)!;
        }
        const parts = tileId.split("/");
        const level = parseInt(parts[0].replace("level", ""));
        const [_, col, row] = parts[1].split("_").map(Number);
        const result: [number, number, number] = [level, col, row];
        this.tileIdCache.set(tileId, result);
        return result;
    }

    private touchTile(tileId: string): void {
        const info = this.activeTiles.get(tileId);
        if (info) {
            this.activeTiles.delete(tileId);
            this.activeTiles.set(tileId, info);
            info.lastAccessed = Date.now();
        }
    }

    hasTile(tileId: string): boolean {
        return this.activeTiles.has(tileId);
    }

    // Getters
    getAtlasTexture(): WebGLTexture {
        return this.atlasTexture;
    }
    getAtlasSize(): number {
        return this.atlasSize;
    }
    getIndirectionTable(): WebGLTexture {
        return this.indirectionTable;
    }

}
